import { lhcVSpan } from "./placement";

/* golaClearance.js — the GOLA is REAL PHYSICAL GEOMETRY (2026-08-22 mandate:
 * "implement a proper GOLA collision and automatic cutout system").
 *
 * A module with a vertical gola (config.golaPos L/R/LR + a chosen profile)
 * carries the profile ON that side edge — it occupies space beside the
 * cabinet. Two consequences, both derived (never persisted):
 *
 *  1. CLEARANCE — a hard 30mm keep-out beside every gola side. The collision
 *     test, the drag clamps and the flush-snap magnets all read pairMinGap()
 *     so a neighbour can never be dragged, dropped or snapped closer than
 *     30mm to the gola side — continuously, while dragging.
 *
 *  2. NOTCH — the neighbour standing at the gola side automatically receives
 *     the STANDARD 18×18mm full-height rebate on its facing front corner
 *     (the same cut the gola cabinet's own gable carries), produced through
 *     the real CAD cut engine (polygon booleans on the carcass + fronts —
 *     never an overlay). It follows the gola: move either cabinet and it
 *     recomputes; remove the gola and it disappears.
 *
 * Pure helpers, metres. Works for base/wall/tall roles, either side, any
 * width/height, chains of modules. */

export const GOLA_SIDE_CLEARANCE_M = 0.030; // hard keep-out beside a gola side
/* "unit on top" reach for the Auto-connect extent — identical to golaRuns */
export const GOLA_POST_REACH_M = 0.15;

/** golaPostExtentM(p, def, placements, effDefOf) -> { bot, top } WORLD metres:
 *  the vertical extent of p's gola post(s). Default = the module's own band;
 *  cfg.golaHmm extends the top to bottom + H; cfg.golaTop === "wall" (Auto
 *  connect) runs it up to the TOP of the units above (same wall, span
 *  overlap, standing at/above the module's top) — the same detection
 *  buildGolaPosts uses, so the keep-out follows the drawn profile exactly.
 *  2026-08-30 user: "even wall units should hold the 30mm as soon as we make
 *  the gola height connect — everything the gola touches adds exactly 30mm". */
export function golaPostExtentM(p, def, placements, effDefOf) {
  const g = golaSidesOf(p, def);
  if (!g.L && !g.R) return null;
  const band = lhcVSpan(p, def);
  const cfg = p.config || {};
  let top = band[1];
  const hOv = Number(cfg.golaHmm) || 0;
  if (hOv > 0) top = Math.max(top, band[0] + hOv / 1000);
  else if (cfg.golaTop === "wall") {
    const a0 = Number(p.absLeft) || 0, a1 = a0 + (def.width || 0);
    (placements || []).forEach((q) => {
      if (!q || q === p || q.id === p.id) return;
      if ((q.wallId || null) !== (p.wallId || null)) return;
      const qd = effDefOf(q);
      if (!qd) return;
      const b0 = Number(q.absLeft) || 0, b1 = b0 + (qd.width || 0);
      if (Math.min(a1, b1) - Math.max(a0, b0) <= 0.001) return;
      const qb = lhcVSpan(q, qd);
      if (qb[0] < band[1] - GOLA_POST_REACH_M) return;      // not an "on top" unit
      top = Math.max(top, qb[1]);
    });
  }
  return { bot: band[0], top };
}

/** golaChannels(wallId, placements, effDefOf) -> the 30mm CHANNELS on a wall:
 *  [{ x0, x1, bot, top, carrierId, side }] — one per gola side of every
 *  carrier, [edge, edge + 30mm] (R) / [edge - 30mm, edge] (L), over the post's
 *  full vertical extent. A channel is REAL geometry: nothing whose band the
 *  post passes may stand inside it. */
export function golaChannels(wallId, placements, effDefOf) {
  const out = [];
  (placements || []).forEach((c) => {
    if (!c || (c.wallId || null) !== (wallId || null) || c.island) return;
    const cd = effDefOf(c);
    if (!cd) return;
    const g = golaSidesOf(c, cd);
    if (!g.L && !g.R) return;
    const ext = golaPostExtentM(c, cd, placements, effDefOf);
    if (!ext) return;
    const a0 = Number(c.absLeft) || 0, a1 = a0 + (cd.width || 0);
    if (g.L) out.push({ x0: a0 - GOLA_SIDE_CLEARANCE_M, x1: a0, bot: ext.bot, top: ext.top, carrierId: c.id, side: "L" });
    if (g.R) out.push({ x0: a1, x1: a1 + GOLA_SIDE_CLEARANCE_M, bot: ext.bot, top: ext.top, carrierId: c.id, side: "R" });
  });
  return out;
}

/** golaChannelsFor(p, def, placements, effDefOf) -> the channels that BLOCK
 *  p: on p's wall, not p's own, vertically overlapping p's band (1mm grace). */
export function golaChannelsFor(p, def, placements, effDefOf) {
  if (!p || !def || !p.wallId || p.island) return [];
  const band = lhcVSpan(p, def);
  return golaChannels(p.wallId, placements, effDefOf).filter((ch) =>
    ch.carrierId !== p.id && ch.bot < band[1] - 1e-3 && ch.top > band[0] + 1e-3);
}

/** golaGapBetween(a, b, placements, effDefOf) -> the gola gap REQUIRED between
 *  consecutive same-wall members a (left) and b (right): 30mm when a facing
 *  side carries a gola directly (pairMinGapLR) OR when an extended post's
 *  channel from ANY carrier sits in their joint and reaches their band (the
 *  wall units above a connected gola). The pair-gap rulebook and the filler
 *  schedule subtract it, so a 30mm gola gap reads as FLUSH — never a
 *  dead-band, never a filler, never a worktop break. */
export function golaGapBetween(a, b, placements, effDefOf) {
  if (!a || !b) return 0;
  const da = effDefOf(a), db = effDefOf(b);
  if (!da || !db) return 0;
  if (pairMinGapLR(a, da, b, db) > 0) return GOLA_SIDE_CLEARANCE_M;
  if (!a.wallId || (a.wallId || null) !== (b.wallId || null)) return 0;
  const aR = (Number(a.absLeft) || 0) + (da.width || 0);
  const bL = Number(b.absLeft) || 0;
  const tol = GOLA_SIDE_CLEARANCE_M + ADJ_TOL_M;
  const ba = lhcVSpan(a, da), bb = lhcVSpan(b, db);
  const hit = golaChannels(a.wallId, placements, effDefOf).some((ch) => {
    if (ch.carrierId === a.id || ch.carrierId === b.id) return false;
    if (ch.x0 < aR - tol || ch.x1 > bL + tol) return false;         // not in their joint
    const reachA = ch.bot < ba[1] - 1e-3 && ch.top > ba[0] + 1e-3;
    const reachB = ch.bot < bb[1] - 1e-3 && ch.top > bb[0] + 1e-3;
    return reachA || reachB;
  });
  return hit ? GOLA_SIDE_CLEARANCE_M : 0;
}

/** channelIntrusion(p, def, ch) -> metres of p standing INSIDE ch (> 0 = violation) */
export function channelIntrusion(p, def, ch) {
  const a0 = Number(p.absLeft) || 0, a1 = a0 + (def.width || 0);
  return Math.min(a1, ch.x1) - Math.max(a0, ch.x0);
}
export const GOLA_NOTCH_MM = 18;            // the 18×18 standard cut
const ADJ_TOL_M = 0.012;                    // "standing at the gola" capture beyond the clearance

/** does this placement carry a PHYSICAL vertical gola on each side?
 *  Mirrors buildGolaPosts's own gating: floor-standing roles, a chosen
 *  profile, an explicit golaPos. */
export function golaSidesOf(p, def) {
  const out = { L: false, R: false };
  if (!p || !def) return out;
  const role = def.role;
  if (role !== "cabinet-base" && role !== "cabinet-tall") return out;
  const cfg = p.config || {};
  if (!cfg.gola || cfg.gola === "None") return out;
  const pos = String(cfg.golaPos || "");
  out.L = pos === "L" || pos === "LR";
  out.R = pos === "R" || pos === "LR";
  return out;
}

/** minimum edge gap (m) between two SAME-WALL modules a (left) and b (right):
 *  0.03 when a's RIGHT side or b's LEFT side carries a gola, else 0. */
export function pairMinGapLR(aLeft, defA, bRight, defB) {
  const ga = golaSidesOf(aLeft, defA);
  const gb = golaSidesOf(bRight, defB);
  return (ga.R || gb.L) ? GOLA_SIDE_CLEARANCE_M : 0;
}

/** per-side clearance of ONE placement — {left, right} in metres. */
export function sideClearances(p, def) {
  const g = golaSidesOf(p, def);
  return { left: g.L ? GOLA_SIDE_CLEARANCE_M : 0, right: g.R ? GOLA_SIDE_CLEARANCE_M : 0 };
}

/** which sides of `p` need the automatic 18×18 notch: a same-wall neighbour
 *  whose FACING side carries a gola stands within the clearance band.
 *  effDefOf(q) → def. Overlapping layers both count (base beside tall etc. —
 *  vertical golas span the carcass, so any same-wall adjacency notches). */
export function golaNotchSides(p, def, placements, effDefOf) {
  const out = { L: false, R: false };
  if (!p || !def) return out;
  /* OWN SIDES (2026-08-26 user mandate: "so we can implement it here without,
   * like, cutting manually the notch — we click on gola and it puts the gola
   * based on its size, and it does a notch in the module"). The module that
   * CARRIES the gola gets the rebate on its own gable, not just its
   * neighbour: that is the whole point of picking a profile. (This reverses
   * the 2026-08-24o "only ME will cut" rule at the same user's request; the
   * manual Cut -> GOLA Cut feature stays untouched for special cases.)
   * Island / wall-less modules reach this too — they have no wallId and used
   * to return early before any side was considered. */
  const own = golaSidesOf(p, def);
  out.L = own.L; out.R = own.R;
  if (!p.wallId) return out;
  const a0 = Number(p.absLeft) || 0;
  const a1 = a0 + (def.width || 0);
  (placements || []).forEach((q) => {
    if (!q || q === p || q.id === p.id || q.wallId !== p.wallId) return;
    const qd = effDefOf(q);
    if (!qd) return;
    const g = golaSidesOf(q, qd);
    if (!g.L && !g.R) return;
    const b0 = Number(q.absLeft) || 0;
    const b1 = b0 + (qd.width || 0);
    // q to the RIGHT of p with a gola on q's LEFT → notch p's RIGHT edge
    if (g.L && b0 >= a1 - 1e-6 && b0 - a1 <= GOLA_SIDE_CLEARANCE_M + ADJ_TOL_M) out.R = true;
    // q to the LEFT of p with a gola on q's RIGHT → notch p's LEFT edge
    if (g.R && b1 <= a0 + 1e-6 && a0 - b1 <= GOLA_SIDE_CLEARANCE_M + ADJ_TOL_M) out.L = true;
  });
  return out;
}

/** golaRebateSides(p, def, placements, effDefOf, blob) — which sides of `p`
 *  actually get MACHINED. Own gola sides always; a NEIGHBOUR's gola side only
 *  when that neighbour's profile is DOUBLE-SIDED.
 *
 *  2026-08-26, from the user's own handoff: "A single-sided gola never
 *  triggers this — it seats against a fixed side panel, not another cabinet,
 *  so there's never a second panel to notch." A double-sided profile has a lip
 *  on both edges and bridges the joint, so it needs a pocket in BOTH facing
 *  gables; a single-sided one does not, and notching the neighbour there left
 *  an 18mm slot with nothing in it.
 *
 *  Double-sided is measured, not declared: the profile must span the 30mm
 *  keep-out AND still land ~12mm into each gable — 30 + 24 = 54mm. His two
 *  real uploads sit either side of that (48.3mm "Teke"/single, 66.8mm
 *  "Dopio"/double). golaNotchSides is UNCHANGED and still drives the
 *  hinge/handle automation for every gola. */
export const GOLA_DOUBLE_MIN_M = 0.055;

/** golaRebateSides(p, def, placements, effDefOf, acrossOf, cornerOf)
 *
 *  WORLD-SPACE adjacency (2026-08-26 user: "MAKE THE GOLA FULLY AUTO NOTCH
 *  WORK ON THE CORNER"). The old rule compared absLeft on the SAME wall, so an
 *  L-kitchen got nothing: the two runs meet on different walls at different
 *  angles and every same-wall test failed. Now the test is the only one that
 *  is true in both cases — do the two modules' FRONT CORNERS on their facing
 *  sides land on the same spot?
 *    · straight run  — a gola side pushes its neighbour to the 30mm keep-out,
 *      so the two front corners sit 30mm apart;
 *    · inside corner — run A's front-left corner and run B's front-right
 *      corner are the SAME point, 0mm apart.
 *  One distance test covers both, plus islands, angled walls and peninsulas,
 *  because it never looks at a wall id at all.
 *
 *  cornerOf(placement, def, "L"|"R") → {x, z} world metres, or null when the
 *  caller cannot place it (then that placement is simply skipped). acrossOf
 *  (placement) → the chosen profile's across-the-wall width; a NEIGHBOUR is
 *  only pocketed for a DOUBLE-sided profile — from the user's own handoff, "a
 *  single-sided gola never triggers this — it seats against a fixed side
 *  panel, not another cabinet". golaNotchSides is UNCHANGED and still drives
 *  the hinge/handle automation for every gola. */
export function golaRebateSides(p, def, placements, effDefOf, acrossOf, cornerOf) {
  const own = golaSidesOf(p, def);
  const res = { L: own.L, R: own.R };
  if (!p || !def || typeof acrossOf !== "function" || typeof cornerOf !== "function") return res;
  const near = GOLA_SIDE_CLEARANCE_M + ADJ_TOL_M;
  const mine = { L: cornerOf(p, def, "L"), R: cornerOf(p, def, "R") };
  if (!mine.L || !mine.R) return res;
  (placements || []).forEach((q) => {
    if (!q || q === p || q.id === p.id) return;
    const qd = effDefOf(q);
    if (!qd) return;
    const g = golaSidesOf(q, qd);
    if (!g.L && !g.R) return;
    if (!(acrossOf(q) >= GOLA_DOUBLE_MIN_M)) return;      // single-sided: no neighbour pocket
    ["L", "R"].forEach((gs) => {
      if (!g[gs]) return;
      const c = cornerOf(q, qd, gs);
      if (!c) return;
      ["L", "R"].forEach((ms) => {
        const m = mine[ms];
        if (Math.hypot(m.x - c.x, m.z - c.z) <= near) res[ms] = true;
      });
    });
  });
  return res;
}

/** the derived cut prisms (cabinetCuts schema, METRES, cabinet-local frame:
 *  x centre-0/+right, z centre-0/+front, y from body bottom) for the notched
 *  sides — the STANDARD pocket: 18mm wide, 18mm deep measured from the
 *  DOOR-BACK plane (front − 22mm — where the gola physically lives; the
 *  2026-08-24 CSG diag proved real uploaded carcasses END at that plane, so
 *  a front-anchored prism cut nothing), open through the front face, full
 *  height, extended 1mm past the rim so the boolean cuts cleanly. */
export const GOLA_DOOR_ZONE_M = 0.022;      // ≡ golaRuns.GOLA_FACE_RECESS_M
/** golaNotchCuts(def, sides, opts) — opts.depthM overrides the standard 18mm
 *  pocket depth with the CHOSEN PROFILE's real section depth when it is
 *  deeper (2026-08-26: the uploaded profiles measure 20.07mm, so a fixed 18mm
 *  rebate left 2mm of the profile buried in solid gable). The 18mm WIDTH is
 *  the standard and never changes — "the gola place is 18mm by 18mm". */
export function golaNotchCuts(def, sides, opts) {
  const out = [];
  if (!def) return out;
  const W = def.width || 0, D = def.depth || 0;
  const n = GOLA_NOTCH_MM / 1000, over = 0.001;
  /* THE POCKET IS THE PROFILE (2026-08-26, his drawings): cut exactly the
   * chosen profile's own depth so it fills the rebate with no free space —
   * his double is 17mm deep, and flooring at the 18mm "standard" left a 1mm
   * void behind it. The 18mm floor still applies when no profile depth is
   * known, which is the only case it was ever meant for. */
  const want = (opts && Number(opts.depthM)) || 0;
  const dp = want > 0 ? want : n;
  const zBack = D / 2 - GOLA_DOOR_ZONE_M - dp;  // the pocket, behind the door back
  const mk = (x1, x2, id) => ({
    id,
    points: [[x1, zBack], [x2, zBack], [x2, D / 2 + over], [x1, D / 2 + over]],
    yFrom: 0, yTo: 100,
    derived: true, golaNotch: true,
  });
  if (sides && sides.L) out.push(mk(-W / 2 - over, -W / 2 + n, "__golaNotchL"));
  if (sides && sides.R) out.push(mk(W / 2 - n, W / 2 + over, "__golaNotchR"));
  return out;
}
